import { useState, useEffect } from 'react'
import { useAuth } from '@/lib/contexts/AuthContext'
import { reportAttributionFailure } from '@/lib/observability/attribution'
import { API_V1_URL } from '@/lib/api/api'

export interface CampaignBreakdown {
  devices: { name: string; sessions: number }[]
  countries: { name: string; sessions: number }[]
  browsers: { name: string; sessions: number }[]
  os: { name: string; sessions: number }[]
  pages: { name: string; sessions: number }[]
  contents: { name: string; sessions: number }[]
}

export interface AdsAttributionCampaign {
  /** Names joined with " + " when several ad campaigns share one utm_campaign */
  campaign: string
  /** Set when several ad campaigns share this utm_campaign and so one row */
  merged_campaigns?: string[]
  utm_campaign: string
  platforms: string[]
  target_urls: string[]
  top_landing_page: string | null
  spend: number
  impressions: number
  clicks: number
  ad_conversions: number
  sessions: number
  /** Payments, renewals included */
  revenue_conversions: number
  /** Distinct paying customers (one first-touch view each) */
  revenue_customers?: number
  revenue: number
  trial_count: number
  roas: number | null
  /** Spend per paying customer */
  true_cpa: number | null
  /** Form submission events matched to this campaign's tagged sessions */
  form_submissions?: number
  cost_per_trial: number | null
  click_to_session_rate: number | null
  avg_hours_to_convert: number | null
}

export interface AdsAttributionTotals {
  /** Every ad campaign's spend, matched or not: the platform total */
  spend: number
  /** Spend of campaigns matched to tagged visits. Impressions, clicks,
   *  sessions, trials and payments below cover these campaigns only. */
  matched_spend?: number
  /** Spend of campaigns no tagged visit arrived from */
  unmatched_spend?: number
  impressions: number
  clicks: number
  sessions: number
  revenue: number
  /** Payments, renewals included */
  revenue_conversions: number
  /** Distinct paying customers */
  revenue_customers?: number
  trial_count: number
  form_submissions?: number
  roas: number | null
  avg_hours_to_convert: number | null
  /** Number of conversions the avg_hours_to_convert is based on — show tile only when ≥ 5 */
  time_to_convert_sample_size?: number
}

export interface UnmatchedAdCampaign {
  campaign: string
  platforms: string[]
  target_urls: string[]
  spend: number
  impressions: number
  clicks: number
  note: string
}

export interface UnmatchedUtmCampaign {
  utm_campaign: string
  top_landing_page: string | null
  sessions: number
  revenue_conversions: number
  revenue: string
  trial_count: number
  note: string
}

export interface AdsAttributionData {
  campaigns: AdsAttributionCampaign[]
  totals: AdsAttributionTotals
  unmatched_ad_campaigns: UnmatchedAdCampaign[]
  unmatched_utm_campaigns: UnmatchedUtmCampaign[]
  breakdowns: Record<string, CampaignBreakdown>
  date_range: { startDate: string; endDate: string }
  connections: { google: number; meta: number }
  googleAdsHealth?: 'ok' | 'stale' | 'expired'
}

export function useAdsAttribution(
  propertyId: string | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
) {
  const { token } = useAuth()
  const [data, setData] = useState<AdsAttributionData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!propertyId || !token || !startDate || !endDate) return
    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const url = `${API_V1_URL}properties/${propertyId}/ads-attribution?startDate=${startDate}&endDate=${endDate}`
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          credentials: 'include',
        })
        if (!res.ok) {
          throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status })
        }
        const json = await res.json()
        if (!cancelled) setData(json)
      } catch (err: any) {
        reportAttributionFailure('ads', err, { propertyId })
        if (!cancelled) setError(err.message || 'Failed to load ads attribution')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [propertyId, token, startDate, endDate])

  return { data, isLoading, error }
}
